
  import { defineComponent, PropType } from 'vue';
  import { mapActions, mapGetters } from 'vuex';

  import { getPreviousYear, getPreviousYearByYear } from '@/common/utils/year';

  import ModalComponent from '@/components/modals/ModalComponent.vue';

  import {
    REGION_DASHBOARDS_VES_ACTIONS_NAME,
    REGION_DASHBOARDS_VES_GETTERS_NAME,
  } from '../../../store/regionDashboards/name';
  import {
    TDashboardRegionFilter,
    TDashboardRegionFilterPayload,
    TDashboardsRatingCard,
    TDashboardsRatingItem,
  } from '../../../types/regionDashboards';
  import DashboardsCard from '../dashboards-card/DashboardsCard.vue';
  import RegionDashboardsRatingList from './RegionDashboardsRatingList.vue';

  export default defineComponent({
    name: 'RegionDashboardsRatingCards',
    components: {
      DashboardsCard,
      ModalComponent,
      RegionDashboardsRatingList,
    },
    props: {
      filter: {
        type: Object as PropType<TDashboardRegionFilter>,
        required: true,
      },
    },
    data(): {
      year: string;
      ratingCardsAbortController: AbortController;
      ratingListAbortController: AbortController;
      showModal: boolean;
      currentCard: TDashboardsRatingCard | null;
      currentSort: number;
      isLoadingCards: boolean;
      isLoadingList: boolean;
      ratingListFilterCache: Map<
        string,
        {
          filter: TDashboardRegionFilter;
          items: TDashboardsRatingItem[];
        }
      >;
    } {
      return {
        year: '',
        ratingCardsAbortController: new AbortController(),
        ratingListAbortController: new AbortController(),
        showModal: false,
        currentCard: null,
        currentSort: 0,
        isLoadingCards: false,
        isLoadingList: false,
        ratingListFilterCache: new Map(),
      };
    },
    computed: {
      ...mapGetters({
        ratingCards: REGION_DASHBOARDS_VES_GETTERS_NAME.getRatingCards,
        ratingList: REGION_DASHBOARDS_VES_GETTERS_NAME.getRatingList,
      }),
      getRatingListCache() {
        return this.ratingListFilterCache.get(this.currentCard?.title ?? '')?.items ?? [];
      },
      isShowAPPG() {
        return this.year !== '0';
      },
    },
    methods: {
      ...mapActions({
        getRatingCards: REGION_DASHBOARDS_VES_ACTIONS_NAME.getRatingCards,
        getRatingList: REGION_DASHBOARDS_VES_ACTIONS_NAME.getRatingList,
      }),
      getYear() {
        return this.filter.year ? String(getPreviousYearByYear(this.filter.year)) : String(getPreviousYear());
      },
      async updateCards() {
        this.ratingCardsAbortController.abort();
        this.ratingCardsAbortController = new AbortController();

        const payload: TDashboardRegionFilterPayload = {
          params: this.filter,
          abortController: this.ratingCardsAbortController,
        };

        this.isLoadingCards = true;
        await this.getRatingCards(payload);
        this.year = this.getYear();
        this.isLoadingCards = false;
      },
      async selectCard(value: TDashboardsRatingCard, index: number) {
        if (index !== 0) return;

        const cacheItem = this.ratingListFilterCache.get(value.title);

        if (JSON.stringify(cacheItem?.filter) === JSON.stringify(this.filter)) {
          this.showModal = true;
          return;
        }

        this.isLoadingList = true;
        this.showModal = true;
        this.currentCard = value;
        this.currentSort = index;

        this.ratingListAbortController.abort();
        this.ratingListAbortController = new AbortController();
        await this.getRatingList({ params: this.filter, abortController: this.ratingListAbortController });
        this.ratingListFilterCache.set(value.title, {
          filter: { ...this.filter },
          items: [...this.ratingList],
        });
        this.isLoadingList = false;
      },
    },
    watch: {
      filter: {
        handler() {
          this.updateCards();
        },
        immediate: true,
        deep: true,
      },
    },
  });
